import React from 'react';
import * as moment from 'moment';
import { IListItem, IOrderRequest } from '@nkr/models/index';
import { OrganType } from '@nkr/enums/index';
import '@nkr/services/extensions';
import { Utility } from '@nkr/services/utility';
import { HeaderWrapper } from './styles';
import {
    ICellRendererParams, ValueGetterParams,
    IHeaderParams, ColDef, RowNode
} from 'ag-grid-community';
import SortableHeaderComponent from '../center/list/common/sortable-header';
import { Label } from 'react-bootstrap';
import OverlayMenuButton, { IMenuItem } from '@nkr/components/overlay-menu-button';
import CompletionDeadlineCell from './completion-deadline-cell';

const buildTrackingUrl = (trackingNumber: string): string | null => {
    if (!trackingNumber) return null;
    const t = trackingNumber.trim();
    if (t.toUpperCase().startsWith('1Z')) {
        return `https://www.ups.com/track?tracknum=${t}`;
    }
    const isDigits = /^\d+$/.test(t);
    if (isDigits && t.length >= 20) {
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${t}`;
    }
    if (isDigits && t.length >= 12 && t.length <= 15) {
        return `https://www.fedex.com/apps/fedextrack/?tracknumbers=${t}`;
    }
    return null;
};

const FOLLOW_UP_ENTITY_TYPES = new Set([7, 8, 10, 11]); // Donor Cheek, Recipient Cheek, XM, Serology
const NON_REORDERABLE_ENTITY_TYPES = new Set([14, 15]); // Strongbox, Strongbox OPO
const COMPLETION_DEADLINE_ENTITY_TYPES = new Set([4, 10, 11]); // D Serology, XM, S Serology
const DEFAULT_COMPLETION_DEADLINE_BUSINESS_DAYS = 2;

export const useOrderTrackingColumns = (
    isAdmin: boolean,
    centerId: number,
    kitTypes: IListItem[],
    shipVendors: IListItem[],
    orderStatuses: IListItem[],
    organType: OrganType,
    onCancel?: (id: number) => void,
    onReorder?: (id: number) => void,
    onFollowUpEmail?: (id: number) => void,
    onCompletionDeadlineSelected?: (id: number, deadline: string) => void
): ColDef[] => {
    // Mirrors the "--" placeholder the collection/surgery date valueGetters emit, sorting blanks last.
    const nullableDateComparator = (date1: string, date2: string, nodeA: RowNode, nodeB: RowNode, isInverted: boolean) => {
        if (date1 === "--" && date2 === "--") return 0;
        if (date1 === "--") return isInverted ? -1 : 1;
        if (date2 === "--") return isInverted ? 1 : -1;
        return moment(date1, 'MM/DD/YYYY').isBefore(moment(date2, 'MM/DD/YYYY')) ? -1 : 1;
    };

    const statusComparator = (status1: number, status2: number) => {
        let statusName = getStatus(status1);
        let statusName2 = getStatus(status2);

        if (statusName < statusName2) {
            return -1;
        }
        if (statusName > statusName2) {
            return 1;
        }
        return 0;

    };

    const kitTypeComparator = (status1: number, status2: number, node1: any, node2: any) => {
        let statusName = getKitTypes(status1);
        let statusName2 = getKitTypes(status2);

        if (statusName < statusName2) {
            return -1;
        }
        if (statusName > statusName2) {
            return 1;
        }
        let label = node1?.data?.kitTypeLabel || getKitTypeShortLabel(status1);
        let label2 = node2?.data?.kitTypeLabel || getKitTypeShortLabel(status2);
        return label.localeCompare(label2);
    };

    const getShipVendors = (vendorId: number) => {
        return shipVendors.find(x => x.id === vendorId)?.description || 'N/A';
    };

    const getStatus = (statusId: number) => {
        return orderStatuses.find(x => x.id === statusId)?.description || 'N/A';
    };

    const getKitTypes = (kitTypeId: number) => {
        return kitTypes.find(x => x.id === kitTypeId)?.description || 'N/A';
    };

    // Short display labels for the Kit Type grid column
    const KIT_TYPE_SHORT_LABELS: { [id: number]: string } = {
        0: 'PWL',
        1: 'HBD',
        3: 'Donor',
        4: 'D Serology',
        5: 'Recipient',
        7: 'Cheek Swab',
        8: 'Cheek Swab',
        9: 'Antibody',
        10: 'XM',
        11: 'S Serology',
        13: 'Bulk Cheek',
        14: 'SB',
        15: 'SB OPO',
    };

    const getKitTypeShortLabel = (kitTypeId: number) => {
        return KIT_TYPE_SHORT_LABELS[kitTypeId] || getKitTypes(kitTypeId);
    };

    // Entity type groups for donor/recipient logic
    const serologyXmTypes = [10, 11]; // SEROLOGY_TRACKING, XM_TRACKING
    const donorTypes = [0, 1, 3, 7]; // DASH, HOME_BLOOD_DRAW, DONOR, DONOR_CHEEK_SWAB_ORDER
    const recipientTypes = [5, 8, 9, 12]; // RECIPIENT, RECP_CHEEK_SWAB_ORDER, ANTIBODY_SCREENING, PATIENT_INTAKE (legacy)

    const getStatusBadgeStyle = (statusId: number) => {
        switch (statusId) {
            case 1: return 'primary';      // ORDERED
            case 2: return 'info';         // SHIPPED_TO_DONOR (In transit)
            case 3: return 'info';         // DELIVERED_TO_DONOR
            case 4: return 'danger';       // SHIPPED_TO_NKR_LAB (Returned)
            case 5: return 'success';      // DELIVERED_TO_NKR_LAB (Received)
            case 0: return 'danger';       // CANCELLED
            case -2: return 'warning';     // ON_HOLD (Pending)
            default: return 'default';
        }
    };

    const isDonorOrRecipientKit = (entityType: number) => {
        const isSeriologyXm = serologyXmTypes.includes(entityType);
        const isDonor = donorTypes.includes(entityType);
        const isRecipient = recipientTypes.includes(entityType);
        return { isSeriologyXm, isDonor, isRecipient };
    };

    const columns: ColDef[] = [
        // 1. R# (row number, not a data field)
        {
            colId: '1',
            headerName: 'R#',
            headerComponentFramework: (props: IHeaderParams) => <div>R#</div>,
            sortable: false,
            pinned: 'left',
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },

        // Org (Organ Type: K = Kidney, L = Liver)
        {
            field: 'organType',
            headerName: 'Org',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Org</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 55,
            maxWidth: 70,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;
                return <>{data.organType == OrganType.Liver ? 'L' : 'K'}</>;
            }
        },

        // 2. Kit Type
        {
            field: 'entityType',
            headerName: 'Kit Type',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Kit Type</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 130,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;
                let typeName = data.kitTypeLabel || getKitTypeShortLabel(data.entityType);
                return <Label bsStyle='default' style={{ fontSize: '100%' }}>{typeName}</Label>
            },
            comparator: kitTypeComparator
        },

        // 3. Status
        {
            field: 'statusId',
            headerName: 'Status',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Status</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 120,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;
                var statusName = getStatus(data.statusId);
                var badgeStyle = getStatusBadgeStyle(data.statusId);
                return <Label bsStyle={badgeStyle} style={{ fontSize: '100%' }}>{statusName}</Label>
            },
            comparator: statusComparator
        },

        // 4. Donor Center (for serology/XM + donor-type kits; "--" for recipient-only)
        {
            field: 'donorCenterAlias',
            headerName: 'Donor Center',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Donor<br />Center</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 150,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IOrderRequest;
                let types = isDonorOrRecipientKit(data.entityType);

                if (types.isRecipient && !types.isSeriologyXm) {
                    return "--";
                }
                if (data.donorCenterAlias) {
                    return <Label bsStyle='default' style={{ fontSize: '100%' }}>{data.donorCenterAlias}</Label>
                }
                return "--";
            }
        },

        // 5. Donor Alias (link to donor profile when URL exists)
        {
            field: 'donorAlias',
            headerName: 'Donor Alias',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Donor<br />Alias</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 150,
            comparator: (valueA: string, valueB: string, nodeA: any, nodeB: any) => {
                const getAlias = (node: any): string => {
                    const data = node?.data;
                    if (!data) return '';
                    const { isRecipient, isSeriologyXm } = isDonorOrRecipientKit(data.entityType);
                    if (isRecipient && !isSeriologyXm) return '';
                    return (data.donorAlias && data.donorAlias.length > 0) ? data.donorAlias : '';
                };
                const a = getAlias(nodeA);
                const b = getAlias(nodeB);
                if (!a && !b) return 0;
                if (!a) return -1;
                if (!b) return 1;
                return a.toLowerCase().localeCompare(b.toLowerCase());
            },
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;
                let types = isDonorOrRecipientKit(data.entityType);

                if (types.isRecipient && !types.isSeriologyXm) {
                    return "--";
                }
                if (data.donorAlias && data.donorAlias.length > 0) {
                    if ((centerId == data.donorCenterId || isAdmin) && data.donorUrl) {
                        return <a href={`/center/${data.donorCenterId}${data.donorUrl}`}>{data.donorAlias}</a>;
                    } else {
                        return <>{data.donorAlias}</>
                    }
                }
                return "--";
            }
        },

        // 6. Swap # (badge, linked via swapUrl)
        {
            field: 'swapNumber',
            headerName: 'Swap #',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Swap #</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 120,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IOrderRequest;
                if (!data.swapNumber) {
                    return "--";
                }
                return <Label bsStyle='primary' style={{ fontSize: '100%' }}>
                    {data.swapUrl != null
                        ? <a style={{ color: "white" }} href={data.swapUrl} role="button" target="_blank">{data.swapNumber}</a>
                        : data.swapNumber}
                </Label>;
            }
        },

        // 7. Recipient Center
        {
            field: 'patientCenterAlias',
            headerName: 'Recipient Center',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Recipient<br />Center</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 150,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IOrderRequest;
                let types = isDonorOrRecipientKit(data.entityType);

                if (types.isDonor && !types.isSeriologyXm) {
                    return "--";
                }
                if (data.patientCenterAlias != null) {
                    return <Label bsStyle='danger' style={{ fontSize: '100%' }}>{data.patientCenterAlias}</Label>
                }
                return "--";
            }
        },

        // 8. Recipient Alias (link to recipient profile when URL exists)
        {
            field: 'patientAlias',
            headerName: 'Recipient Alias',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Recipient<br />Alias</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 150,
            comparator: (valueA: string, valueB: string, nodeA: any, nodeB: any) => {
                const getAlias = (node: any): string => {
                    const data = node?.data;
                    if (!data) return '';
                    const { isDonor, isSeriologyXm } = isDonorOrRecipientKit(data.entityType);
                    if (isDonor && !isSeriologyXm) return '';
                    return (data.patientAlias && data.patientAlias.length > 0) ? data.patientAlias : '';
                };
                const a = getAlias(nodeA);
                const b = getAlias(nodeB);
                if (!a && !b) return 0;
                if (!a) return -1;
                if (!b) return 1;
                return a.toLowerCase().localeCompare(b.toLowerCase());
            },
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;
                let types = isDonorOrRecipientKit(data.entityType);

                if (types.isDonor && !types.isSeriologyXm) {
                    return "--";
                }
                if (data.patientAlias != null && data.patientAlias.length > 0) {
                    if ((centerId == data.patientCenterId || isAdmin) && data.patientUrl) {
                        return <a href={`/center/${data.patientCenterId}${data.patientUrl}`}>{data.patientAlias}</a>;
                    } else {
                        return <>{data.patientAlias}</>
                    }
                }
                return "--";
            }
        },

        // 9. Vendor/Phlebot Svc (Yes/No badge)
        {
            field: 'phlebotRequired',
            headerName: 'Vendor/Phlebot Svc',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Vendor/<br />Phlebot Svc</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 120,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;
                if (data.phlebotRequired) {
                    return <Label bsStyle='success' style={{ fontSize: '100%' }}>Yes</Label>;
                } else {
                    return <Label bsStyle='default' style={{ fontSize: '100%' }}>No</Label>;
                }
            }
        },

        // 10. Vendor
        {
            field: 'shippingVendorId',
            headerName: 'Vendor',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Vendor</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 100,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IOrderRequest;
                var vendorName = getShipVendors(data.shippingVendorId);
                var badgeStyle = data.shippingVendorId === 1 ? 'primary' : 'default'; // DS2 = 1
                return <Label bsStyle={badgeStyle} style={{ fontSize: '100%' }}>{vendorName}</Label>
            }
        },

        // 11. Requested Date
        {
            field: 'orderDateTime',
            headerName: 'Requested Date',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Requested<br />Date</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 110,
            valueGetter: (params: ValueGetterParams) => moment(params.data.orderDateTime).format('MM/DD/YYYY'),
            comparator: Utility.dateComparator
        },

        // 12. Collection Date (blood draw; only serology kits capture one)
        {
            field: 'collectionDate',
            headerName: 'Collection Date',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Collection<br />Date</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 110,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IOrderRequest;
                return data.collectionDate ? moment(data.collectionDate).format('MM/DD/YYYY') : "--";
            },
            comparator: nullableDateComparator
        },

        // Collection Deadline (admin-set date, else collection date + 2 business days; serology and XM only)
        {
            field: 'completionDeadline',
            headerName: 'Collection Deadline',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Collection<br />Deadline</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 110,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IOrderRequest;
                if (!COMPLETION_DEADLINE_ENTITY_TYPES.has(data.entityType)) {
                    return "--";
                }
                if (data.completionDeadline) {
                    return moment(data.completionDeadline).format('MM/DD/YYYY');
                }
                if (data.collectionDate) {
                    return moment(data.collectionDate).addBusinessDays(DEFAULT_COMPLETION_DEADLINE_BUSINESS_DAYS).format('MM/DD/YYYY');
                }
                return "--";
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IOrderRequest;
                if (isAdmin && COMPLETION_DEADLINE_ENTITY_TYPES.has(data.entityType)) {
                    return <CompletionDeadlineCell {...params} onCompletionDeadlineSelected={onCompletionDeadlineSelected} />;
                }
                return <>{params.value}</>;
            },
            comparator: nullableDateComparator
        },

        // 13. Surgery Date (donor kidney pickup for donor-backed kits; scheduled donation date for Streamlined Serology; recipient transplant date for Antibody)
        {
            field: 'surgeryDate',
            headerName: 'Surgery Date',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Surgery<br />Date</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 110,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IOrderRequest;
                return data.surgeryDate ? moment(data.surgeryDate).format('MM/DD/YYYY') : "--";
            },
            comparator: nullableDateComparator
        },

        // 14. Tracking (Outbound)
        {
            field: 'outboundTrackingNumber',
            headerName: 'Tracking (Outbound)',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Tracking<br />(Outbound)</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 130,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;
                if (data.outboundTrackingNumber != null && data.outboundTrackingNumber.length > 0) {
                    const url = buildTrackingUrl(data.outboundTrackingNumber) || data.trackingUrl;
                    if (url) {
                        return <a href={url} target="_blank">{data.outboundTrackingNumber}</a>
                    }
                    return <>{data.outboundTrackingNumber}</>;
                } else {
                    return "--";
                }
            }
        },

        // 15. Tracking (Return)
        {
            field: 'inboundTrackingToNkrLab',
            headerName: 'Tracking (Return)',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Tracking<br />(Return)</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 130,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;
                if (data.inboundTrackingToNkrLab != null && data.inboundTrackingToNkrLab.length > 0) {
                    const inboundUrl = buildTrackingUrl(data.inboundTrackingToNkrLab);
                    if (inboundUrl) {
                        return <a href={inboundUrl} target="_blank">{data.inboundTrackingToNkrLab}</a>;
                    }
                    return <>{data.inboundTrackingToNkrLab}</>;
                } else {
                    return "--";
                }
            }
        },

        // 16. Results Available
        {
            field: 'resultsAvailable',
            headerName: 'Results Available',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Results<br />Available</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: true,
            minWidth: 100,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;
                if (data.resultsAvailable && data.resultsUrl) {
                    const href = data.resultsUrl.startsWith('/center/')
                        ? data.resultsUrl
                        : `/center/${data.patientCenterId}${data.resultsUrl}`;
                    return <a href={href} title="View Results" style={{ color: '#5cb85c', fontSize: '18px', fontWeight: 'bold' }}>✓</a>;
                }
                return null;
            }
        },

        // 17. Actions
        {
            field: 'id',
            headerName: 'Actions',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <HeaderWrapper>Actions</HeaderWrapper>
                </SortableHeaderComponent>,
            sortable: false,
            minWidth: 110,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IOrderRequest;

                if (!data.id) {
                    return null;
                }

                const ACTION_CANCEL = 1;
                const ACTION_REORDER = 2;
                const ACTION_FOLLOW_UP = 3;
                const cancellableStatuses = new Set([-2, -1, 1]); // ON_HOLD, PENDING_REQUEST, ORDERED
                const showFollowUp = FOLLOW_UP_ENTITY_TYPES.has(data.entityType);
                const showReorder = isAdmin && !NON_REORDERABLE_ENTITY_TYPES.has(data.entityType);
                const menu: IMenuItem[] = [
                    { id: data.id, action: ACTION_CANCEL, include: isAdmin && cancellableStatuses.has(data.statusId), selectable: true, element: <span>Cancel</span> },
                    { id: data.id, action: ACTION_REORDER, include: showReorder, selectable: true, element: <span>Reorder</span> },
                    { id: data.id, action: ACTION_FOLLOW_UP, include: showFollowUp, selectable: true, element: <span>Follow-up Email</span> },
                ];
                const handleSelect = (id: any, action: number) => {
                    if (action === ACTION_CANCEL) { onCancel && onCancel(id); }
                    else if (action === ACTION_REORDER) { onReorder && onReorder(id); }
                    else if (action === ACTION_FOLLOW_UP) { onFollowUpEmail && onFollowUpEmail(id); }
                };
                return (
                    <OverlayMenuButton
                        menu={menu}
                        bsStyle="primary"
                        bsSize="xs"
                        title="Actions"
                        includeCaret={true}
                        onSelect={handleSelect}
                    />
                );
            }
        }
    ];

    return organType == OrganType.Liver ? columns.filter(x => x.field != 'swapNumber' && x.field != 'organType') : columns;
};